import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import CommandPalette from './CommandPalette';
import runProposal from './runProposal';

const SHORTCUT_KEY = 'k';
const NOOP = Object.freeze({ openPalette: () => {} });
const CommandPaletteContext = createContext(NOOP);

/**
 * NOTIF1 T17: the one Ctrl/⌘K palette of the teacher shell (N6, client-side).
 * `handlers` = App navigation callbacks for runProposal; read through a ref so callers may pass a new object each render.
 */
export function CommandPaletteProvider({ enabled, students, families, stats, handlers, children }) {
  const [open, setOpen] = useState(false);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === SHORTCUT_KEY) {
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled]);

  const close = useCallback(() => setOpen(false), []);
  const run = useCallback((proposal) => runProposal(proposal, handlersRef.current), []);
  const value = useMemo(() => ({ openPalette: () => setOpen(true) }), []);

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      {enabled && open ? (
        <CommandPalette students={students} families={families} stats={stats} onRun={run} onClose={close} />
      ) : null}
    </CommandPaletteContext.Provider>
  );
}

export const useCommandPalette = () => useContext(CommandPaletteContext);
